import throttle from 'lodash/throttle';
import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { StyleSheet, Text, View } from 'react-native';

import {
  useConsumerQuery,
  useInitiateResetEmailMutation,
} from 'apollo/queries/autogenerate/hooks';
import { PinCodeDispatchMethod } from 'apollo/queries/autogenerate/schemas';
import Button from 'shared/modules/common/components/atoms/Button';
import TextInput from 'shared/modules/common/components/atoms/TextInput';
import validator, { rules } from 'shared/utils/validator';

const FIELD_NAME = 'newEmail';

type FormProps = {
  newEmail: string;
};

type Props = {
  onSubmit: (email: string) => void;
};

const useFormWithValidations = (props: FormProps) => {
  const form = useForm<FormProps>({
    defaultValues: props,
  });

  useEffect(() => {
    form.register(
      {
        name: FIELD_NAME,
      },
      {
        validate: validator.getRules(['mustBeValidEmail']),
      }
    );
  }, []);

  form.watch();

  return form;
};

const EditUserEnterEmail = (props: Props) => {
  const form = useFormWithValidations({ [FIELD_NAME]: '' });
  const { data } = useConsumerQuery({ fetchPolicy: 'cache-only' });
  const [initiateEmailMutation] = useInitiateResetEmailMutation();
  const [errorMessage, setErrorMessage] = useState('');

  const validateEmailThenSubmit = async () => {
    const { isError, message } = rules.mustNotBeSame(
      form.getValues().newEmail,
      data?.consumer.email || '',
      'メールアドレス'
    );

    if (isError) {
      setErrorMessage(message);

      return;
    }

    const isValid = await form.triggerValidation(FIELD_NAME);

    if (isValid) {
      setErrorMessage('');
      form.clearError(FIELD_NAME);
      invokeOTP();
    }
  };

  // Sends an OTP code to user's phone
  // So this can be invoked to request a new OTP
  const invokeOTP = throttle(
    async () => {
      try {
        const { newEmail } = form.getValues();

        await initiateEmailMutation({
          variables: {
            newEmail,
            pinCodeDispatchMethod: PinCodeDispatchMethod.VOICE,
          },
        });

        props.onSubmit(newEmail);
      } catch (err) {
        setErrorMessage('入力内容を確認してください');
      }
    },
    2000,
    { leading: true, trailing: false }
  );

  return (
    <>
      <View style={style.root}>
        <View style={style.content}>
          <Text style={style.subHeading}>
            新しいメールアドレスを入力してください。
          </Text>
          <View style={style.spacer} />
          <TextInput
            label="新しいメールアドレス"
            placeholder="hello@paidy.com"
            value={form.getValues(FIELD_NAME)}
            onChangeText={(text) => form.setValue(FIELD_NAME, text)}
            onFocus={() => {
              setErrorMessage('');
              form.clearError(FIELD_NAME);
            }}
            onBlur={() => {
              setErrorMessage('');
              form.clearError(FIELD_NAME);
              form.triggerValidation(FIELD_NAME);
            }}
            message={
              (form.errors[FIELD_NAME]?.message as string) || errorMessage || ''
            }
            state={
              form.errors[FIELD_NAME]?.message || errorMessage
                ? 'error'
                : undefined
            }
            id="edit-user-email-new-email-field"
          />
          <View style={style.spacer} />
        </View>
        <Button
          title="次へ"
          style={style.button}
          onPress={validateEmailThenSubmit}
          id="edit-user-email-next-button"
        />
      </View>
    </>
  );
};

const style = StyleSheet.create({
  root: {
    flex: 1,
  },
  content: {
    flexGrow: 1,
  },
  spacer: {
    marginBottom: 24,
  },
  subHeading: {
    fontSize: 16,
    lineHeight: 24,
    fontWeight: '300',
  },
  button: {
    alignSelf: 'center',
  },
});

export default EditUserEnterEmail;
